import { IItemCacheDispatch, IItemCacheThunkAction } from '../../store/actions/ItemCacheActions';
import { IItemCache } from '../IItemCacheObserver';
import { IControlFaceted } from '../../components/decorators/withControlHandler';
import { IItemCacheState } from '../../store/reducers/itemCacheReducer';
import { IItemCacheDemands, IItemCacheDependencies, ItemCacheObserver, memoizeGetCacheItem } from './ItemCacheObserver';
import { itemCacheSuspenseControl } from '../../controls/itemCacheSuspenseControl';
import { asOnceOnly } from '../../store/middleware/RunOnceMiddleware';
import { excludeResolvedDemands } from './excludeResolvedDemands';
import { areShallowDifferent } from '@ms/utilities-objects/lib/index';
import { ComputedItemFacet } from './ComputedItemFacet';
import { KeyFacet, stringPart } from '../KeyFacet';
import { Facet, IFaceted } from '../Facet';
import { bindItemCacheSelectorToProps } from './bindItemCacheSelectorToProps';
import { updateItems } from '../../store/reducers/itemMapReducer';
import { mapObject } from '../../utilities/objects';

const computedItemKeyFacet = new KeyFacet<{ itemKey: string; }>('computedItem', {
  itemKey: stringPart()
});

const computedFacetsFacet = new Facet<IFaceted<IItemCacheSelector<{} | undefined>>>('computedFacets');
const computedFacetsMarkerFacet = new Facet<undefined>('computedFacetsMarker');

/**
 * @internal
 */
export interface IItemCacheSelector<TResult> {
  (itemCache: IItemCache): TResult;
}

/**
 * Represents the variable parameters required when evaluating a memoized ItemCache selector.
 * @internal
 */
export interface IItemCacheEvaluatorEvaluateParams<TResult> {
  /**
   * The selector to evaluate against store state.
   * If possible, results from previous executions of the selector will be re-used,
   * if the state and control handlers match.
   */
  selector: IItemCacheSelector<TResult>;
  /**
   * The control handlers to apply when evaluating the selector.
   * These will be composed with any handlers from the store for demanded items.
   */
  controlHandlers: IControlFaceted;
  /**
   * The current state against which to evaluate the selector.
   */
  state: IItemCacheState;
  /**
   * An entity which will be consuming the result of the selector.
   * This can be any object, and is used for debugging only.
   */
  owner: {};
  /**
   * An entity which manages the 'lifetime' of the selector's use.
   * This will be used to handle 'suspense' behaviors since the last
   * execution of any selector needs to be tracked to the same object.
   */
  suspender: {};
}

export interface IItemCacheEvaluatorGetComputedSelectorParams<TResult> {
  state: IItemCacheState;
  computedItemFacet: ComputedItemFacet<TResult>;
  itemKey: string | undefined;
}

/**
 * Represents the consumable results from evaluating and ItemCache selector.
 * @internal
 */
export interface IItemCacheEvaluation<TResult> {
  /**
   * The result of invoking the selector.
   */
  result: TResult;
  /**
   * The demands made while evaluating the selector.
   * These should be issued to a call to getItems.
   */
  demands: IItemCacheDemands;
  /**
   * A snapshot of the values of dependencies for the selector.
   * These should be registered with the cache pruning system.
   */
  dependencies: IItemCacheDependencies;
}

/**
 * Represents a selector and its dependencies for a computed item facet.
 */
export interface IItemCacheComputedFacetSelector<TResult> {
  selector: IItemCacheSelector<TResult | undefined>;
  dependencies: IItemCacheDependencies;
}

/**
 * Represents a memoizer for selector evaluators per-selector.
 * @internal
 */
export interface IItemCacheEvaluator {
  /**
   * Evaluates a selector function in the context of a current state and
   * set of control handlers. The intent is that if the selector has been
   * executed before, the previous evaluation result may be used if the
   * control handlers have not changed, and the subset of state consumed by the
   * selector has not changed.
   */
  evaluate<TResult>(params: IItemCacheEvaluatorEvaluateParams<TResult>): IItemCacheEvaluation<TResult>;

  /**
   * Gets a memoizable selector bound to an item key for a given computed facet.
   */
  getComputedFacetSelector<TResult>(params: IItemCacheEvaluatorGetComputedSelectorParams<TResult>): {
    selector: IItemCacheSelector<TResult | undefined>;
    dependencies: IItemCacheDependencies;
  };

  /**
   * Creates and dispatches an action which promotes the currently-memoized computed facet selectors
   * to the store so that they may have their lifetimes managed by the cache dependency system.
   */
  publishComputedFacets(): void;
}

interface ISelectorEvaluationState<TResult> {
  previousEvaluation?: IItemCacheEvaluation<TResult>;
  controlHandlers?: IControlFaceted;
  evaluationsPerState: WeakMap<IItemCacheState, IItemCacheEvaluation<TResult>>;
}

interface ISuspenderEvaluationState<TResult> {
  selector: IItemCacheSelector<TResult>;
  previousEvaluation?: IItemCacheEvaluation<TResult>;
}

/**
 * Class to hold state regarding the last evaluation for debug access.
 */
class ItemCacheEvaluationDebug {
  private _debug: object;

  constructor(params: object) {
    this._debug = params;
  }

  public toString(): string {
    return JSON.stringify(this._debug);
  }

  public dispose(): void {
    // Do nothing.
  }
}

/**
 * Component which memoizes item cache selectors by using previous evaluations and state
 * information to avoid re-evaluating selectors as much as possible.
 */
class ItemCacheEvaluator implements IItemCacheEvaluator {
  private _dispatch: IItemCacheDispatch;

  private _selectorEvaluations: WeakMap<{}, ISelectorEvaluationState<{}>>;
  private _suspenderEvaluations: WeakMap<{}, ISuspenderEvaluationState<{}>>;

  private _selectors: {
    [itemKey: string]: IFaceted<IItemCacheSelector<{} | undefined>>;
  };

  constructor(dispatch: IItemCacheDispatch) {
    this._dispatch = dispatch;

    this._selectorEvaluations = new WeakMap();
    this._suspenderEvaluations = new WeakMap();

    this._selectors = {};
  }

  /**
   * Evaluates an item cache selector on behalf of a given owner, for a given store state and
   * in the context of a set of control handlers.
   *
   * This is all one large function to aid debuggability when stepping through the code.
   */
  public evaluate<TResult>(params: IItemCacheEvaluatorEvaluateParams<TResult>): IItemCacheEvaluation<TResult> {
    const {
      selector,
      controlHandlers,
      state,
      owner,
      suspender
    } = params;

    const selectorEvaluationState = this._getSelectorEvaluationState(selector);
    const suspenderEvaluationState = this._getSuspenderEvaluationState(selector, suspender);

    const previousSelector = suspenderEvaluationState.selector;

    const hasSelectorChanged = selector !== previousSelector ? {
      previous: previousSelector,
      current: selector
    } : false;

    const previousControlHandlers = selectorEvaluationState.controlHandlers;

    const haveControlHandlersChanged = areShallowDifferent(controlHandlers, previousControlHandlers, { debug: true });

    if (!haveControlHandlersChanged) {
      // If the selector is being executed against the same set of control handlers,
      // then check to see if it is also being run against a previous state.
      // If so, short-circuit.
      const evaluationForState = selectorEvaluationState.evaluationsPerState.get(state);

      if (evaluationForState) {
        suspenderEvaluationState.previousEvaluation = evaluationForState;

        return evaluationForState;
      }
    } else {
      // Invalidate the result from the previous evaluation of the selector.
      selectorEvaluationState.previousEvaluation = undefined;
      selectorEvaluationState.controlHandlers = controlHandlers;
    }

    const previousSelectorEvaluation = selectorEvaluationState.previousEvaluation;
    const previousSuspenderEvaluation = suspenderEvaluationState.previousEvaluation;

    let evaluation = previousSelectorEvaluation || suspenderEvaluationState.previousEvaluation;

    const previousDemands = previousSuspenderEvaluation && previousSuspenderEvaluation.demands;

    const itemCache = new ItemCacheObserver({
      controlHandlers,
      dispatch: this._dispatch,
      owner,
      previousDemands: previousSelectorEvaluation && previousSelectorEvaluation.demands,
      previousDependencies: previousSelectorEvaluation && previousSelectorEvaluation.dependencies,
      state,
      evaluator: this
    });

    try {
      let haveDependenciesChanged: boolean | undefined;

      if (!previousSelectorEvaluation || (haveDependenciesChanged = itemCache.haveDependenciesChanged())) {
        // Try to invoke the selector as infrequently as possible.
        // Only invoke if the props have changed, the control handlers have changed, or the cache dependencies
        // have changed.

        // IF YOU WANT TO DIAGNOSE WHY A COMPONENT RE-RENDERED, STICK A BREAKPOINT IN THIS FUNCTION
        // AND SET A CONDITION FOR
        // owner.displayName.indexOf('YourComponent') > -1
        // Example:
        // owner.displayName.indexOf('ItemSetDetailsList') > -1

        if (!evaluation || !isDebouncing(itemCache, {
          previousDemands
        })) {
          const lastItemCacheEvaluation = new ItemCacheEvaluationDebug({
            haveControlHandlersChanged,
            hasSelectorChanged,
            haveDependenciesChanged,
            owner
          });

          const {
            result
          } = itemCache.evaluate(selector);

          evaluation = {
            result,
            dependencies: itemCache.getDependencies(),
            demands: itemCache.getDemands()
          };

          selectorEvaluationState.previousEvaluation = evaluation;
          selectorEvaluationState.evaluationsPerState.set(state, evaluation);

          lastItemCacheEvaluation.dispose();
        } else {
          // Clone the demand object so that the demands can be re-issued.
          evaluation = {
            ...evaluation,
            demands: {
              ...(previousDemands || {})
            }
          };
        }
      } else {
        // The selector's dependencies have not changed since last time, so record
        // the last evaluation for the current state.
        selectorEvaluationState.evaluationsPerState.set(state, previousSelectorEvaluation);

        evaluation = previousSelectorEvaluation;
      }
    } finally {
      // This shuts off the ability to consume from the observer beyond the callback,
      // which is important to guard against out-of-band usage, which could cause problems
      // with state tracking.
      itemCache.dispose();
    }

    suspenderEvaluationState.previousEvaluation = evaluation;
    suspenderEvaluationState.selector = selector;

    return evaluation;
  }

  public getComputedFacetSelector<TResult>(
    params: IItemCacheEvaluatorGetComputedSelectorParams<TResult>
  ): {
      selector: IItemCacheSelector<TResult | undefined>;
      dependencies: IItemCacheDependencies;
    } {
    const {
      computedItemFacet,
      itemKey,
      state
    } = params;

    const computedItemKey = computedItemKeyFacet.serialize({
      itemKey: itemKey || ''
    });

    const item = memoizeGetCacheItem(state)(computedItemKey);

    const cachedSelectors = this._selectors[computedItemKey] ||
      (this._selectors[computedItemKey] = {} as IFaceted<IItemCacheSelector<{} | undefined>>);

    const selectors = item && computedFacetsFacet.unpack(item) || cachedSelectors;

    const facetId = computedItemFacet.id;

    const selector = cachedSelectors[facetId] = (selectors[facetId] as IItemCacheSelector<TResult | undefined>) ||
      bindItemCacheSelectorToProps(computedItemFacet.compute, {
        itemKey
      });

    const dependencies: IItemCacheDependencies = {
      // Register a dependency on this wrapped item onto a facet which will never change.
      // This ensures that the selectors for the bound item are protected from pruning
      // while they are in use, but adding selectors does not trigger a re-evaluation of dependent selectors.
      [computedItemKey]: computedFacetsMarkerFacet.pack(undefined)
    };

    return {
      selector: selector,
      dependencies
    };
  }

  public publishComputedFacets(): void {
    const selectors = this._selectors;

    this._selectors = {};

    this._dispatch(updateItems({
      items: mapObject(selectors, (value: IFaceted<IItemCacheSelector<{} | undefined>>) => {
        return computedFacetsFacet.pack(value);
      })
    }));
  }

  private _getSelectorEvaluationState<TResult>(
    selector: IItemCacheSelector<TResult>
  ): ISelectorEvaluationState<TResult> {
    const memoizer = this._selectorEvaluations;

    let evaluationState = memoizer.get(selector) as ISelectorEvaluationState<TResult> | undefined;

    if (!evaluationState) {
      evaluationState = {
        evaluationsPerState: new WeakMap()
      };

      memoizer.set(selector, evaluationState);
    }

    return evaluationState;
  }

  private _getSuspenderEvaluationState<TResult>(
    selector: IItemCacheSelector<TResult>,
    suspender: {}
  ): ISuspenderEvaluationState<TResult> {
    const memoizer = this._suspenderEvaluations;

    let evaluationState = memoizer.get(suspender) as ISuspenderEvaluationState<TResult> | undefined;

    if (!evaluationState) {
      evaluationState = {
        selector
      };

      memoizer.set(suspender, evaluationState);
    }

    return evaluationState;
  }
}

/**
 * Gets a memoizer for item cache selectors, which uses previous evaluations and state
 * information to avoid re-evaluating selectors as much as possible.
 * @internal
 */
export const getItemCacheEvaluator: IItemCacheThunkAction<IItemCacheEvaluator> =
  asOnceOnly((dispatch: IItemCacheDispatch) => {
    return new ItemCacheEvaluator(dispatch);
  });

/**
 * Determines whether or not the given selector is in 'suspense'
 */
function isDebouncing(itemCache: IItemCache, props: {
  previousDemands?: IItemCacheDemands;
}): boolean {
  const suspenseControlHandler = itemCache.demandItemFacet(itemCacheSuspenseControl, undefined);

  const {
    mode
  } = itemCacheSuspenseControl.evaluate(suspenseControlHandler)(itemCache, {});

  const { previousDemands } = props;

  return mode === 'debounce' && !!previousDemands && Object.keys(excludeResolvedDemands(itemCache, {
    items: previousDemands
  }).items).length > 0;
}
